import {
  Box,
  Button,
  Chip,
  Divider,
  Typography,
  Container,
  Backdrop,
  Modal,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";

import CustomDialog from "./CustomDialog";
import CustomSnackbar from "./CustomSnackbar";
import PageLoader from "./PageLoader";
import { getAllSettings, getSettingsByModule, parseSettingRows } from "../utils/api";
import {
  createModuleSync,
  updateModuleSync,
  deleteModuleSync,
} from "../utils/moduleSync";
import { fetchModuleFieldSummaries, fetchSyncableModules } from "../utils/zoho";
import { diffModuleAndDatabaseFields } from "../utils/fields";
import { SYNC_DURATIONS } from "../utils/constants";
import {
  BORDER,
  BORDER_DARK,
  BORDER_LIGHT,
  BRAND_COLOR,
  BRAND_COLOR_DARK,
  BRAND_COLOR_TEXT,
  BRAND_COLOR_TINT,
  CARD_SHADOW_HOVER,
  CHIP_NEUTRAL_BG,
  SURFACE,
  SURFACE_TINT,
  TEXT_MUTED,
  TEXT_ON_DARK,
  TEXT_SECONDARY,
} from "../utils/colors";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  // width: 400,
  bgcolor: "background.paper",
  // border: "2px solid #000",
  boxShadow: 24,
  borderRadius: 5,
  p: 4,
};

const Settings = ({ orgId, apiKey, connectionUrl, dataCenterUrl, zapiKey }) => {
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [fetchedModules, setFetchedModules] = useState({});
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentModuleFields, setCurrentModuleFields] = useState([]);
  const [currentDatabaseFields, setCurrentDatabaseFields] = useState([]);
  const [syncDuration, setSyncDuration] = useState(null);
  const [deleteBackupData, setDeleteBackupData] = useState(false);
  const [modules, setModules] = useState(null);
  const [moduleDetails, setModuleDetails] = useState("");
  const [modulesToBeSaved, setModulesToBeSaved] = useState({});
  const [pageMode, setPageMode] = useState("");
  const [showBackdrop, setShowBackDrop] = useState(false);

  const [deleteSettingId, setDeleteSettingId] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const auth = { orgId, apiKey };

  const handleOpenModal = (id) => {
    setDeleteSettingId(id);
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setDeleteSettingId();
    setOpenModal(false);
  };

  //snackbar state and functions
  const [openSnack, setOpenSnack] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState(""); // success or error

  const showError = (errorMessage) => {
    setMessage(
      typeof errorMessage === "string" ? errorMessage : JSON.stringify(errorMessage)
    );
    setSeverity("error");
    handleClickSnack();
  };

  useEffect(() => {
    if (!selectedModule) return;
    //if it exists in the fetchedModules then grab info from there
    if (fetchedModules[selectedModule.api_name]) {
      if (pageMode === "addNewMode") {
        setCurrentModuleFields(fetchedModules[selectedModule.api_name]);
      }
      return;
    }

    (async () => {
      try {
        const resposeFetchModuleFields = await fetchModuleFieldSummaries(
          selectedModule.api_name
        );

        if (resposeFetchModuleFields.data) {
          if (pageMode === "addNewMode") {
            setCurrentModuleFields(resposeFetchModuleFields.data);
          }
          setFetchedModules((prev) => ({
            ...prev,
            [selectedModule.api_name]: resposeFetchModuleFields.data,
          }));
        }
      } catch (err) {
        showError(err);
      }
    })();
  }, [selectedModule]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const settingRows = await getAllSettings(auth);

        const moduleNeedToSave = {};
        parseSettingRows(settingRows).forEach((row) => {
          moduleNeedToSave[row.module_name] = {
            ...row.setting_schema,
            setting_id: row.setting_id,
          };
        });

        setModulesToBeSaved(moduleNeedToSave);
      } catch (err) {
        showError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    try {
      setLoading(true);
      fetchSyncableModules().then(setModules);
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSyncDuration("");
    setSelectedModule(null);
    setCurrentModuleFields([]);
    setCurrentDatabaseFields([]);
  };

  const resetDialogState = () => {
    setOpen(false);
    setSelectedModule(null);
    setCurrentModuleFields([]);
    setCurrentDatabaseFields([]);
    setSyncDuration(null);
    setDeleteBackupData(false);
    setPageMode("");
  };

  const handleAddNewSave = async () => {
    const settingSchema = {
      moduleInfo: {
        api_name: selectedModule.api_name,
        module_name: selectedModule.plural_label,
      },
      moduleDetails: moduleDetails,
      database_fields: currentDatabaseFields,
      syncDuration: syncDuration,
      deleteBackupData: deleteBackupData,
    };

    const { error, settingId } = await createModuleSync({
      auth,
      connectionUrl,
      dataCenterUrl,
      zapiKey,
      moduleApiName: selectedModule.api_name,
      settingSchema,
      syncDurationMinutes: syncDuration.total_in_minutes,
    });

    if (error) {
      showError(error);
      return false;
    }

    setModulesToBeSaved((prev) => ({
      ...prev,
      [selectedModule.api_name]: { ...settingSchema, setting_id: settingId },
    }));
    return true;
  };

  const handleEditSave = async () => {
    const existingSettings = await getSettingsByModule(auth, selectedModule.api_name);
    const settingId = existingSettings?.[0]?.setting_id;

    if (!settingId) {
      showError("Setting id not found");
      return false;
    }

    const settingSchema = {
      moduleInfo: {
        api_name: selectedModule.api_name,
        module_name: selectedModule.module_name,
      },
      moduleDetails: moduleDetails,
      database_fields: currentDatabaseFields,
      syncDuration: syncDuration,
      deleteBackupData: deleteBackupData,
    };

    const { error } = await updateModuleSync({
      auth,
      connectionUrl,
      dataCenterUrl,
      zapiKey,
      moduleApiName: selectedModule.api_name,
      settingId,
      settingSchema,
      syncDurationMinutes: syncDuration.total_in_minutes,
    });

    if (error) {
      showError(error);
      return false;
    }

    setModulesToBeSaved((prev) => ({
      ...prev,
      [selectedModule.api_name]: { ...settingSchema, setting_id: settingId },
    }));
    return true;
  };

  const handleSave = async () => {
    setShowBackDrop(true);
    try {
      const saved =
        pageMode === "addNewMode"
          ? await handleAddNewSave()
          : await handleEditSave();

      if (saved) {
        resetDialogState();
      }
    } catch (err) {
      showError(err?.message || err);
    } finally {
      // always end the loading state, even if a step above threw
      setShowBackDrop(false);
    }
  };

  const handleClickSnack = () => {
    setOpenSnack(true);
  };
  //snackbar
  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const handleDeleteSetting = async (module) => {
    try {
      setDeleteLoading(true);

      const { error } = await deleteModuleSync({
        auth,
        connectionUrl,
        moduleApiName: module?.moduleInfo?.api_name,
        settingId: module.setting_id,
      });

      if (error) {
        showError(error);
        setShowBackDrop(false);
        return;
      }

      setModulesToBeSaved((prev) => {
        const remainingModules = {};
        Object.values(prev).forEach((moduleInfo) => {
          if (moduleInfo.setting_id !== module.setting_id) {
            remainingModules[moduleInfo.moduleInfo.module_name] = moduleInfo;
          }
        });
        return remainingModules;
      });

      handleCloseModal();
    } catch (err) {
      showError("Connection Url is wrong!");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditModule = async (module) => {
    setShowBackDrop(true);

    const responseFetchModuleFields = await fetchModuleFieldSummaries(
      module?.moduleInfo.api_name
    );

    // if something went wrong
    if (responseFetchModuleFields.error) {
      setShowBackDrop(false);
      return;
    }

    const { availableModuleFields, markedDatabaseFields } =
      diffModuleAndDatabaseFields(
        responseFetchModuleFields.data,
        module?.database_fields
      );

    setPageMode("editMode");

    setTimeout(() => {
      setFetchedModules((prev) => ({
        ...prev,
        [module?.moduleInfo.api_name]: responseFetchModuleFields.data,
      }));
      setSelectedModule(module?.moduleInfo);
      setSyncDuration(module?.syncDuration);
      setDeleteBackupData(module?.deleteBackupData);

      setCurrentDatabaseFields(markedDatabaseFields);
      setCurrentModuleFields(availableModuleFields);
      handleClickOpen();
      setShowBackDrop(false);
    }, 100);
  };

  const handleModuleChange = (event, value) => {
    setSelectedModule(value);
    setCurrentDatabaseFields([]);
  };

  if (loading)
    return (
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <PageLoader message="Loading modules..." />
      </Container>
    );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Backdrop
        sx={{ color: TEXT_ON_DARK, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CustomSnackbar
        open={openSnack}
        handleClose={handleCloseSnack}
        message={message}
        severity={severity}
      />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
            Modules
          </Typography>
          <Typography sx={{ fontSize: 14, color: TEXT_MUTED }}>
            Zoho CRM modules synced to your MySQL database
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          sx={{
            bgcolor: BRAND_COLOR,
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: BRAND_COLOR_DARK },
          }}
          onClick={() => {
            setPageMode("addNewMode");
            setSelectedModule(null);
            setSyncDuration({
              label: "60 Mins",
              total_in_minutes: 60,
            });
            setDeleteBackupData(true);
            setCurrentDatabaseFields(null);
            setCurrentModuleFields(null);
            handleClickOpen();
          }}
        >
          Add New Module
        </Button>
      </Box>

      {Object.keys(modulesToBeSaved).length === 0 ? (
        <Box
          sx={{
            py: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: BRAND_COLOR_TINT,
              mb: 1,
            }}
          >
            <TableChartRoundedIcon sx={{ fontSize: 30, color: BRAND_COLOR }} />
          </Box>
          <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
            No modules configured yet
          </Typography>
          <Typography sx={{ fontSize: 14, color: TEXT_MUTED }}>
            Add a module to start syncing its records to your MySQL database.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 2,
          }}
        >
          {Object.values(modulesToBeSaved)?.map((module) => {
            return (
              <Box
                key={module?.moduleInfo.module_name}
                sx={{
                  borderRadius: "10px",
                  border: `1px solid ${BORDER_LIGHT}`,
                  bgcolor: SURFACE,
                  p: 2.5,
                  transition: "box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: CARD_SHADOW_HOVER,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: BRAND_COLOR_TINT,
                      flexShrink: 0,
                    }}
                  >
                    <TableChartRoundedIcon
                      sx={{ fontSize: 21, color: BRAND_COLOR }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 16, fontWeight: 600 }}
                      noWrap
                    >
                      {module?.moduleInfo?.module_name}
                    </Typography>
                    {module.moduleDetails && (
                      <Typography
                        sx={{ fontSize: 12.5, color: TEXT_MUTED }}
                        noWrap
                      >
                        {module.moduleDetails}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={`${module?.database_fields?.length || 0} fields`}
                    sx={{
                      fontSize: 12,
                      bgcolor: CHIP_NEUTRAL_BG,
                      color: TEXT_SECONDARY,
                    }}
                  />
                  {module?.syncDuration?.label && (
                    <Chip
                      size="small"
                      icon={<ScheduleRoundedIcon sx={{ fontSize: 14 }} />}
                      label={`Every ${module.syncDuration.label}`}
                      sx={{
                        fontSize: 12,
                        bgcolor: BRAND_COLOR_TINT,
                        color: BRAND_COLOR_TEXT,
                        "& .MuiChip-icon": { color: BRAND_COLOR_TEXT },
                      }}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 1.75 }} />

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "6px",
                      color: TEXT_SECONDARY,
                      borderColor: BORDER,
                      "&:hover": {
                        borderColor: BORDER_DARK,
                        bgcolor: SURFACE_TINT,
                      },
                    }}
                    onClick={() => handleEditModule(module)}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                    }
                    sx={{ textTransform: "none", borderRadius: "6px" }}
                    onClick={async () => handleOpenModal(module)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box
            sx={{
              bgcolor: "white",
              width: 500,
            }}
          >
            {" "}
            <Typography sx={{ mt: 4, fontWeight: "bold", fontSize: 16 }}>
              The related workflow won't work anymore if you delete this
              settings. Do you wish to proceed?
            </Typography>
            <Box
              sx={{
                mt: 3,
                ml: "1rem",
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
              }}
            >
              <Button
                sx={{ width: 120 }}
                variant="outlined"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>

              <Button
                disabled={deleteLoading}
                sx={{ width: 120 }}
                variant="contained"
                color="error"
                onClick={() => handleDeleteSetting(deleteSettingId)}
              >
                {deleteLoading ? (
                  <CircularProgress sx={{ color: "white", ml: 1 }} size={16} />
                ) : (
                  "Proceed"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      {/* dialog */}

      {pageMode === "editMode" ? (
        <CustomDialog
          selectedModule={selectedModule}
          mode={pageMode}
          open={open}
          handleClose={handleClose}
          modules={modules.filter(
            (module) => !modulesToBeSaved[module.api_name]
          )}
          handleModuleChange={handleModuleChange}
          currentModuleFields={currentModuleFields}
          setCurrentModuleFields={setCurrentModuleFields}
          currentDatabaseFields={currentDatabaseFields}
          setCurrentDatabaseFields={setCurrentDatabaseFields}
          syncDuration={syncDuration}
          setSyncDuration={setSyncDuration}
          deleteBackupData={deleteBackupData}
          setDeleteBackupData={setDeleteBackupData}
          syncDurations={SYNC_DURATIONS}
          handleSave={handleSave}
          syncAllFields={() => {
            setCurrentDatabaseFields(fetchedModules[selectedModule.api_name]);
            setTimeout(() => {
              setCurrentModuleFields([]);
            }, 100);
          }}
          unSelectAllFields={() => {
            setCurrentModuleFields(fetchedModules[selectedModule.api_name]);
            setCurrentDatabaseFields([]);
          }}
          handleDeleteField={(value) => {
            const filteredCurrentDatabaseFields = currentDatabaseFields.filter(
              (field) => field.api_name !== value.api_name
            );

            setCurrentDatabaseFields([...filteredCurrentDatabaseFields]);
          }}
        />
      ) : (
        <CustomDialog
          open={open}
          mode={pageMode}
          selectedModule={selectedModule}
          handleClose={handleClose}
          modules={modules?.filter(
            (module) => !modulesToBeSaved[module.api_name]
          )}
          handleModuleChange={handleModuleChange}
          currentModuleFields={currentModuleFields}
          syncAllFields={() => {
            setCurrentDatabaseFields([
              ...fetchedModules[selectedModule.api_name],
            ]);
            setTimeout(() => {
              setCurrentModuleFields([]);
            }, 100);
          }}
          unSelectAllFields={() => {
            setCurrentModuleFields([
              ...fetchedModules[selectedModule.api_name],
            ]);

            setCurrentDatabaseFields([]);
          }}
          syncDurations={SYNC_DURATIONS}
          setCurrentModuleFields={setCurrentModuleFields}
          currentDatabaseFields={currentDatabaseFields}
          setCurrentDatabaseFields={setCurrentDatabaseFields}
          syncDuration={syncDuration}
          setSyncDuration={setSyncDuration}
          deleteBackupData={deleteBackupData}
          setDeleteBackupData={setDeleteBackupData}
          handleSave={handleSave}
        />
      )}
    </Container>
  );
};

export default Settings;
